/* eslint-disable no-unused-vars */

import { useEffect } from 'react';
import copyMagicLog from 'common/components/CopyMagic/shared/copyMagicLog';
import useChatSessionId from 'common/components/CopyMagic/hooks/useChatSessionId';
import { STORAGE_EXPIRATION_HOURS, STORAGE_KEY } from './constants';
import type { MessagesType } from '../../contexts/MessagesContext/MessagesContext.types';

type StorageType = {
  messages: MessagesType;
  expiration: Date;
};

const isValidMessage = ([key, value]: [key: any, value: any]): boolean => {
  if (!key) return false;
  if (!value?.createdAt) return false;
  if (!value?.data?.content) return false;
  if (!value?.data?.role) return false;
  return true;
};

const handleDecodeError = (): null => {
  localStorage.removeItem(STORAGE_KEY);
  copyMagicLog({
    message: 'useLocalStorageMessageCache: decodeFromStorage error',
    key: STORAGE_KEY,
  });
  return null;
};

const decodeFromStorage = (json: string): StorageType | null => {
  try {
    const rawData = JSON.parse(json);
    const cache: StorageType = {
      expiration: new Date(rawData.expiration),
      messages: new Map(rawData.messages),
    };
    if (Array.from(cache.messages.entries()).every(isValidMessage)) return cache;
    return handleDecodeError();
  } catch {
    return handleDecodeError();
  }
};

const encodeToStorage = (messages: MessagesType): string => {
  const date = new Date();
  return JSON.stringify({
    expiration: date.setHours(date.getHours() + STORAGE_EXPIRATION_HOURS),
    messages: Array.from(messages.entries()),
  });
};

export const resetLocalStorageMessageCache = () => {
  copyMagicLog({
    message: 'useLocalStorageMessageCache: reset',
    key: STORAGE_KEY,
  });
  const messages = new Map();
  localStorage.setItem(STORAGE_KEY, encodeToStorage(messages));
};

export const useLocalStorageMessageCache = ({
  messages,
  replaceMessages,
}: {
  messages: MessagesType;
  replaceMessages: (newMessages: MessagesType) => void;
}) => {
  const { setNewChatSessionId } = useChatSessionId();

  // Hydrate from local storage on mount
  useEffect(() => {
    const item = localStorage.getItem(STORAGE_KEY);
    if (!item) return;

    const cache = decodeFromStorage(item);
    if (!cache) return;

    if (cache.expiration < new Date()) {
      copyMagicLog({
        message: 'useLocalStorageMessageCache: expired',
        key: STORAGE_KEY,
        cache,
      });
      localStorage.removeItem(STORAGE_KEY);
      setNewChatSessionId();
      return;
    }

    copyMagicLog({
      message: 'useLocalStorageMessageCache: hydrate',
      key: STORAGE_KEY,
      cache,
    });
    replaceMessages(cache.messages);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Store messages to local storage on change
  useEffect(() => {
    if (!messages.size) return;

    copyMagicLog({
      message: 'useLocalStorageMessageCache: encode',
      key: STORAGE_KEY,
      messages,
    });
    localStorage.setItem(STORAGE_KEY, encodeToStorage(messages));
  }, [messages]);

  // Listen for local storage changes
  useEffect(() => {
    const listener = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      if (!event.newValue) return;

      const cache = decodeFromStorage(event.newValue);
      if (!cache) return;
      if (messages.size === cache.messages.size) return;

      copyMagicLog({
        message: 'useLocalStorageMessageCache: event',
        key: STORAGE_KEY,
        cache,
      });
      replaceMessages(cache.messages);
    };

    window.addEventListener('storage', listener);
    return () => window.removeEventListener('storage', listener);
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps
};
